import axios from 'axios';
import Swal from 'sweetalert2';
import { SERVICE_URLS } from '../ApiServices/request-handler';
import {
  fundsMenuOptions,
  helpMenuOptions,
  investmentsMenuOptions,
  reportMenuOptions,
  settingsMenuOptions,
  transferMenuOptions,
} from '../config/config';
import store from '../store/store';
import { getItem, setItem } from './storage_services';
import { onLogout, setRefreshTokenMetadata, setUserData } from '../store/slices/commonSlice';
import { setUsername } from '../store/slices/authSlice';
import { resetInvestmentSlice } from '../store/slices/investmentsSlice';
import { resetNotificationSlice } from '../store/slices/notificationSlice';
import { clearFcmToken } from './notification_service';
import { setApplicantType, setClientApplicantId, setClientApplicationId, setClientId, setKsaCorporate, setRejectionMsg } from '../store/slices/constantSlice';

export const showToast = ({ icon, title, footer, image, position = 'bottom-start', props }) => {
  const Toast = Swal.mixin({
    toast: true,
    position,
    showConfirmButton: false,
    timer: 3000,
    customClass: {
      title: 'truncate_title',
      container: 'swap',
    },
    padding: '10px 10px',
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.onmouseenter = Swal.stopTimer;
      toast.onmouseleave = Swal.resumeTimer;
    },
  });

  Toast.fire({
    imageUrl: image,
    icon,
    title,
    footer,
    ...props,
  });
};

export const getContentKeyAccToLang = (data, key, isArabic) => {
  if (data) {
    if (isArabic) return data[key + '_arabic'];
    return data[key];
  }
  return '';
};

export const setToken = (token) => localStorage.setItem('token', token);
export const getToken = () => localStorage.getItem('token');

export const getUser = () => JSON.parse(localStorage.getItem('user'));
export const setUser = (user) => localStorage.setItem('user', JSON.stringify(user));

export const compareDesc = (expiryDateEpoch, currentDate) => {
  const expiryDate = new Date(expiryDateEpoch * 1000);
  return expiryDate.getTime() > currentDate.getTime() ? 0 : 1;
};

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (callback) => {
  refreshSubscribers?.push(callback);
};

const onRefreshed = (token) => {
  refreshSubscribers?.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

export const refreshAccessToken = async () => {
  try {
    const user = store.getState()?.common?.user ?? null;
    if (!user) return getToken();

    const refresh_token_metadata = store.getState()?.common?.refresh_token_metadata ?? null;

    if (!refresh_token_metadata?.refreshToken) return getToken();

    const isExpired = !refresh_token_metadata?.epochExpiry || compareDesc(refresh_token_metadata?.epochExpiry, new Date()) === 1;

    if (!isExpired) return getToken();

    if (isRefreshing) {
      return new Promise((resolve) => {
        subscribeTokenRefresh((token) => {
          resolve(token);
        });
      });
    }
    isRefreshing = true;

    const apiData = {
      refreshToken: refresh_token_metadata.refreshToken,
    };

    const response = await axios.post(
      process.env.REACT_APP_BASE_URL + SERVICE_URLS.refreshToken,
      apiData
    );

    if (response?.data?.status) {
      const data = response.data.result;

      store.dispatch(setUserData({ ...user, ...data }));
      store.dispatch(
        setRefreshTokenMetadata({
          refreshToken: data.refreshToken,
          epochExpiry: data.epochExpiry,
        })
      );
      setToken(data.accessToken);
      onRefreshed(data.accessToken);

      return data.accessToken;
    }
    return '';
  } catch (error) {
    if (error?.response?.status === 401) {
      const user = store.getState()?.common?.user ?? null;
      if (!user) return '';
      const tempUsername = getItem('username');
      store.dispatch(onLogout());
      localStorage.clear();
      setToken('');
      store.dispatch(setUsername(tempUsername ?? null));
      store.dispatch(setUserData(null));
      store.dispatch(setRefreshTokenMetadata(null));
      store.dispatch(resetInvestmentSlice());
      store.dispatch(resetNotificationSlice());
      clearFcmToken();
      store.dispatch(setApplicantType(null));
      store.dispatch(setKsaCorporate(null));
      store.dispatch(setClientApplicationId(null));
      store.dispatch(setClientApplicantId(null));
      store.dispatch(setClientId(null));
      store.dispatch(setRejectionMsg(null));
      setItem('username', tempUsername);
      window.location.href = '/#/funds/available-funds';
    }
    return '';
  } finally {
    isRefreshing = false;
  }
};

export const getDropdownMenuItems = (menu, getContentKey) => {
  switch (menu) {
    case 'investments':
      return investmentsMenuOptions(getContentKey);
    case 'funds':
      return fundsMenuOptions(getContentKey);
    // case 'trading':
    //   return tradingMenuOptions(getContentKey);
    case 'transfers':
      return transferMenuOptions(getContentKey);
    case 'report':
      return reportMenuOptions(getContentKey);
    case 'settings':
      return settingsMenuOptions(getContentKey);
    case 'help':
      return helpMenuOptions(getContentKey);
    default:
      return [];
  }
};

export const formatNumbers = (value, digit = 2) => {
  const num = Number(value);

  if (isNaN(num) || value === null || value === undefined) {
    return '0';
  }

  const [intPart, decPart = ''] = num.toString().split('.');

  // Add commas only if abs value >= 1000
  const formattedInt =
    Math.abs(Number(intPart)) >= 1000
      ? Number(intPart).toLocaleString('en-US')
      : intPart;

  // Truncate decimal without rounding
  const truncatedDec = decPart.slice(0, digit).padEnd(digit, '0');

  // If decimal is all zeros, don't show it
  if (Number(truncatedDec) === 0) {
    return formattedInt;
  }

  return `${formattedInt}.${truncatedDec}`;
};

export function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

export function convertIntoArray(array) {
  const converted = {};
  for (const key in array) {
    const value = array[key];
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      converted[key] = Object.entries(value).map(([id, label]) => ({
        id: id,
        name: label,
      }));
    } else {
      converted[key] = value;
    }
  }
  return converted;
}

export const formatDate = (date) => {
  if (!date) return null;

  let parts = String(date).replace(/\//g, '-').split('-');

  if (parts[0].length === 4) {
    parts = [parts[2], parts[1], parts[0]];
  }

  const [day, month, year] = parts;

  return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`;
};